"use client";

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { useSession } from "next-auth/react";

export interface ImpersonatedUser {
  id: number;
  username: string;
  orgId: number;
  organizationName: string;
  organizationType: string | null;
  permission: string | null;
  payAnnually: boolean;
  timezone: string | null;
  jurisId: number | null;
  jurisName: string | null;
  jurisAbbr: string | null;
}

export interface OriginalUser {
  id: number;
  username: string;
  orgId: number;
  organizationName: string;
  organizationType: string | null;
  permission: string | null;
  payAnnually: boolean;
  timezone: string | null;
  jurisId: number | null;
  jurisName: string | null;
  jurisAbbr: string | null;
}

export interface ImpersonationData {
  isImpersonating: boolean;
  impersonatedUser: ImpersonatedUser | null;
  originalUser: OriginalUser | null;
  impersonatedAccessToken: string | null;
  originalAccessToken: string | null;
}

export interface EffectiveUser {
  id: string;
  username: string;
  orgId: number;
  organizationName: string;
  organizationType: string | null;
  permission: string | null;
  payAnnually: boolean;
  timezone: string | null;
  jurisId: number | null;
  jurisName: string | null;
  jurisAbbr: string | null;
  accessToken: string;
}

interface ImpersonationContextType {
  impersonationData: ImpersonationData;
  startImpersonation: (data: {
    impersonatedUser: ImpersonatedUser;
    originalUser: OriginalUser;
    impersonatedAccessToken: string;
    originalAccessToken: string;
  }) => void;
  endImpersonation: () => void;
  getActiveAccessToken: (sessionToken: string) => string;
}

const IMPERSONATION_STORAGE_KEY = "dadb_impersonation";

const defaultImpersonationData: ImpersonationData = {
  isImpersonating: false,
  impersonatedUser: null,
  originalUser: null,
  impersonatedAccessToken: null,
  originalAccessToken: null,
};

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const [impersonationData, setImpersonationData] = useState<ImpersonationData>(defaultImpersonationData);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as ImpersonationData;
        setImpersonationData(parsed);
      }
    } catch {
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    if (impersonationData.isImpersonating) {
      localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(impersonationData));
    } else {
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    }
  }, [impersonationData]);

  const startImpersonation = useCallback((data: {
    impersonatedUser: ImpersonatedUser;
    originalUser: OriginalUser;
    impersonatedAccessToken: string;
    originalAccessToken: string;
  }) => {
    setImpersonationData({
      isImpersonating: true,
      impersonatedUser: data.impersonatedUser,
      originalUser: data.originalUser,
      impersonatedAccessToken: data.impersonatedAccessToken,
      originalAccessToken: data.originalAccessToken,
    });
  }, []);

  const endImpersonation = useCallback(() => {
    setImpersonationData(defaultImpersonationData);
  }, []);

  const getActiveAccessToken = useCallback((sessionToken: string): string => {
    if (impersonationData.isImpersonating && impersonationData.impersonatedAccessToken) {
      return impersonationData.impersonatedAccessToken;
    }
    return sessionToken;
  }, [impersonationData.isImpersonating, impersonationData.impersonatedAccessToken]);

  return (
    <ImpersonationContext.Provider
      value={{
        impersonationData,
        startImpersonation,
        endImpersonation,
        getActiveAccessToken,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error("useImpersonation must be used within an ImpersonationProvider");
  }
  return context;
}

export function useEffectiveUser(): {
  effectiveUser: EffectiveUser | null;
  isImpersonating: boolean;
  originalUser: OriginalUser | null;
  isLoading: boolean;
} {
  const { data: session, status } = useSession();
  const { impersonationData } = useImpersonation();

  const isLoading = status === "loading";

  if (impersonationData.isImpersonating && impersonationData.impersonatedUser) {
    return {
      effectiveUser: {
        id: impersonationData.impersonatedUser.id.toString(),
        username: impersonationData.impersonatedUser.username,
        orgId: impersonationData.impersonatedUser.orgId,
        organizationName: impersonationData.impersonatedUser.organizationName,
        organizationType: impersonationData.impersonatedUser.organizationType,
        permission: impersonationData.impersonatedUser.permission,
        payAnnually: impersonationData.impersonatedUser.payAnnually,
        timezone: impersonationData.impersonatedUser.timezone,
        jurisId: impersonationData.impersonatedUser.jurisId,
        jurisName: impersonationData.impersonatedUser.jurisName,
        jurisAbbr: impersonationData.impersonatedUser.jurisAbbr,
        accessToken: impersonationData.impersonatedAccessToken ?? "",
      },
      isImpersonating: true,
      originalUser: impersonationData.originalUser,
      isLoading,
    };
  }

  if (session?.user) {
    return {
      effectiveUser: {
        id: session.user.id,
        username: session.user.username,
        orgId: session.user.orgId,
        organizationName: session.user.organizationName,
        organizationType: session.user.organizationType,
        permission: session.user.permission,
        payAnnually: session.user.payAnnually,
        timezone: session.user.timezone,
        jurisId: session.user.jurisId,
        jurisName: session.user.jurisName,
        jurisAbbr: session.user.jurisAbbr,
        accessToken: session.user.accessToken,
      },
      isImpersonating: false,
      originalUser: null,
      isLoading,
    };
  }

  return {
    effectiveUser: null,
    isImpersonating: false,
    originalUser: null,
    isLoading,
  };
}

